import { useCallback } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { intensityShapeState } from '~/state/atoms/intensityToolAtoms';
import {
    measureFamilyState,
    measurementModeState,
} from '~/state/atoms/measurementAtoms';
import { pickedToolState, selectedToolState } from '~/state/atoms/toolsAtoms';
import { MEASURE_FAMILY_OF, MeasureMode } from '~/utils/annotationKinds';

/**
 * The Measure mode over the measurement (distance, angle) and intensity
 * (profile, circle, rectangle) tools: `active` is null under another tool,
 * `select` cancels a placement and arms a mode, `resume` re-arms the active
 * mode or else the family last measured with. The region refusal is not
 * applied here.
 */
export function useMeasureMode() {
    const tool = useRecoilValue(selectedToolState);
    const measurementMode = useRecoilValue(measurementModeState);
    const shape = useRecoilValue(intensityShapeState);
    const intensityMode: MeasureMode = shape === 'line' ? 'profile' : shape;
    const family = useRecoilValue(measureFamilyState);

    const active =
        tool === 'measurement'
            ? measurementMode
            : tool === 'intensity'
              ? intensityMode
              : null;

    const select = useRecoilCallback(
        ({ snapshot, set }) =>
            (mode: MeasureMode) => {
                // Re-arming would leave a placement in progress half drawn.
                snapshot
                    .getLoadable(viewManagerState)
                    .getValue()
                    ?.interactionContext.toolManager.cancelActivePlacementDraft();
                set(measureFamilyState, MEASURE_FAMILY_OF[mode]);
                if (mode === 'distance' || mode === 'angle') {
                    set(measurementModeState, mode);
                    set(pickedToolState, 'measurement');
                } else {
                    set(
                        intensityShapeState,
                        mode === 'profile' ? 'line' : mode
                    );
                    set(pickedToolState, 'intensity');
                }
            },
        []
    );

    const current =
        active ?? (family === 'size' ? measurementMode : intensityMode);
    const resume = useCallback(() => select(current), [select, current]);

    return { active, select, resume };
}
